import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp } from "lucide-react";
import MediaImage from "@/components/MediaImage";
import Sparkline from "@/components/Sparkline";
import { milestoneLabel } from "@/milestones";
import { relativeTime } from "@/motion";
import { SectionHead, mono, num } from "@/components/home/Primitives";

export const PAGE = 25;
const SORTS = { hype: (r) => r.signal_score ?? -1, age: (r) => r.age ?? 99, gap: (r) => r.gap ?? 999, minutes: (r) => (r.minutes_spark || []).reduce((a, b) => a + b, 0) };

// Couleur sur la colonne Cap UNIQUEMENT : rouge à 1, orange à 2, neutre au-delà.
export function capClass(gap) {
  if (gap === 1) return "bg-[#FF4D4D]";
  if (gap === 2) return "bg-[#FFA629]";
  return "bg-[var(--wl-muted)]";
}

// Progression vers la graduation : barre seule (la couleur signale déjà l'imminence), alignée à gauche.
export function CapBar({ r, t, testid, compact }) {
  if (r.gap == null || !r.threshold) return <span className={`${num} inline-block text-left text-[11px] text-[var(--wl-muted)]`} data-testid={testid}>—</span>;
  const pct = Math.min(100, Math.round((100 * (r.apps || 0)) / r.threshold));
  return (
    <span className="inline-flex items-center" title={t("home.cap_title", { n: r.gap, apps: r.apps, thr: r.threshold })} data-testid={testid}>
      <span className={`block ${compact ? "w-12" : "w-20"} h-2 shrink-0 overflow-hidden rounded-full bg-[var(--wl-line)]`} aria-hidden="true">
        <span className={`block h-full rounded-full ${capClass(r.gap)}`} style={{ width: `${Math.max(pct, pct > 0 ? 8 : 0)}%` }} />
      </span>
    </span>
  );
}

const field = "h-7 border border-[var(--wl-line)] bg-[var(--wl-panel)] px-2 wl-mono text-[10px] uppercase text-[var(--wl-text)] focus:border-[var(--wl-accent)] focus:outline-none";
const select = `${field} rr-select cursor-pointer pr-7`;

export default function WatchTable({ rows, leagues, total, t, lang, upsell }) {
  const navigate = useNavigate();
  const [sort, setSort] = useState({ key: "hype", dir: -1 });
  const [f, setF] = useState({ league: "", age: "", hype: "", q: "" });
  const [page, setPage] = useState(0);
  const maxHype = Math.max(1, ...rows.map((r) => r.signal_score || 0));

  const view = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    const out = rows.filter((r) => (!f.league || String(r.league_id) === f.league) && (!f.age || (r.age ?? 99) <= Number(f.age))
      && (!f.hype || (r.signal_score || 0) >= Number(f.hype)) && (!q || `${r.name} ${r.club_name}`.toLowerCase().includes(q)));
    const get = SORTS[sort.key];
    return out.sort((a, b) => (get(a) - get(b)) * sort.dir);
  }, [rows, f, sort]);
  const pages = Math.max(1, Math.ceil(view.length / PAGE));
  const slice = view.slice(page * PAGE, page * PAGE + PAGE);
  const toggle = (key) => { setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: key === "hype" || key === "minutes" ? -1 : 1 })); setPage(0); };
  const upd = (k, v) => { setF({ ...f, [k]: v }); setPage(0); };
  const Th = ({ k, label, right }) => (
    <th className={`select-none py-1.5 ${right ? "text-right" : "text-left"} ${k ? "cursor-pointer hover:text-[var(--wl-text)]" : ""}`} onClick={k ? () => toggle(k) : undefined} data-testid={k ? `wt-sort-${k}` : undefined} aria-sort={k && sort.key === k ? (sort.dir > 0 ? "ascending" : "descending") : undefined}>
      <span className="inline-flex items-center gap-0.5">{label}{k && sort.key === k && (sort.dir > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}</span>
    </th>
  );

  return (
    <section data-testid="home-watchtable">
      <SectionHead title={`${t("nav.watchlist")} · ${total}`} link="/watchlist" linkLabel={t("home.preview_link", { n: total })} testid="home-watchtable-title" />
      <div className="my-1.5 flex flex-wrap items-center gap-1.5" data-testid="wt-filters">
        <select className={select} value={f.league} onChange={(e) => upd("league", e.target.value)} data-testid="wt-filter-league">
          <option value="">{t("home.f_league")}</option>{leagues.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select className={select} value={f.age} onChange={(e) => upd("age", e.target.value)} data-testid="wt-filter-age">
          <option value="">{t("home.f_age")}</option>{[17, 18, 19, 20, 21].map((a) => <option key={a} value={a}>≤ {a}</option>)}
        </select>
        <select className={select} value={f.hype} onChange={(e) => upd("hype", e.target.value)} data-testid="wt-filter-hype">
          <option value="">{t("home.f_hype")}</option>{[40, 50, 60, 70, 80].map((h) => <option key={h} value={h}>≥ {h}</option>)}
        </select>
        <input className={`${field} min-w-[140px] flex-1 normal-case`} placeholder={t("home.f_search")} value={f.q} onChange={(e) => upd("q", e.target.value)} data-testid="wt-filter-search" />
      </div>
      {upsell}
      {/* ≥ 768 px : tableau dense, lignes de 44 px */}
      <div className="hidden md:block">
        <table className="w-full border-collapse text-[12.5px]" data-testid="wt-table">
          <thead className={`${mono} border-b border-[var(--wl-line)]`}>
            <tr><Th label="#" right /><Th label={t("home.col_player")} /><Th label={t("home.col_club")} /><Th k="age" label={t("home.col_age")} right /><Th k="hype" label="Hype" right /><Th k="minutes" label={t("home.col_minutes")} right /><Th k="gap" label={t("home.col_cap_apps")} /><Th label={t("home.col_last")} /></tr>
          </thead>
          <tbody>
            {slice.map((r, i) => (
              <tr key={r.player_id} onClick={() => navigate(`/players/${r.player_id}?p=${r.provider}`)} data-testid={`wt-row-${r.player_id}`}
                className={`h-11 cursor-pointer border-b border-[var(--wl-line)]/60 transition-colors hover:bg-[var(--wl-panel2)] ${i % 2 ? "bg-[var(--wl-panel)]/40" : ""}`}>
                <td className={`${num} w-8 pr-2 text-right text-[var(--wl-muted)]`}>{page * PAGE + i + 1}</td>
                <td className="max-w-[200px] pr-2"><span className="flex items-center gap-2"><MediaImage src={r.club_logo} alt={t("media.logo_alt", { club: r.club_name || "—" })} kind="club" size={24} radius="rounded-sm" surface="watchlist" imgClassName="object-contain" /><span className="truncate font-bold text-[var(--wl-text)]">{r.name}</span></span></td>
                <td className="max-w-[140px] truncate pr-2 text-[var(--wl-muted)]">{r.club_name}</td>
                <td className={`${num} pr-2 text-right`}>{r.age ?? "—"}</td>
                <td className="relative w-20 pr-2 text-right">
                  <span className="absolute inset-y-2 left-0 bg-[var(--wl-accent)]/10" style={{ width: `${(100 * (r.signal_score || 0)) / maxHype}%` }} aria-hidden="true" />
                  <span className={`${num} relative font-bold text-[var(--wl-text)]`}>{r.signal_score}</span>
                </td>
                <td className="w-20 pr-2 text-right">{r.minutes_spark?.length > 1 ? <Sparkline points={r.minutes_spark} width={60} height={20} testid={`wt-spark-${r.player_id}`} /> : <span className={mono}>—</span>}</td>
                <td className="whitespace-nowrap pr-2 text-left"><CapBar r={r} t={t} testid={`wt-cap-${r.player_id}`} /></td>
                <td className="max-w-[220px] truncate text-[var(--wl-muted)]">{r.last ? <>{milestoneLabel(r.last, t)} <span className={mono}>· {relativeTime(r.last.detected_at, lang)}</span></> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* < 768 px : cartes compactes, 3 colonnes de données maximum */}
      <div className="md:hidden" data-testid="wt-cards">
        {slice.map((r, i) => (
          <button type="button" key={r.player_id} onClick={() => navigate(`/players/${r.player_id}?p=${r.provider}`)} data-testid={`wt-card-${r.player_id}`}
            className={`flex h-11 w-full min-w-0 items-center gap-2 overflow-hidden border-b border-[var(--wl-line)]/60 text-left ${i % 2 ? "bg-[var(--wl-panel)]/40" : ""}`}>
            <span className={`${num} w-6 text-right text-[var(--wl-muted)]`}>{page * PAGE + i + 1}</span>
            <MediaImage src={r.club_logo} alt="" kind="club" size={20} radius="rounded-sm" surface="watchlist" imgClassName="object-contain" />
            <span className="min-w-0 flex-1 overflow-hidden"><span className="block truncate text-[12.5px] font-bold text-[var(--wl-text)]">{r.name}</span><span className="block truncate text-[10px] text-[var(--wl-muted)]">{r.club_name}</span></span>
            <span className={`${num} w-8 text-right font-bold`}>{r.signal_score}</span>
            <span className="shrink-0 pr-1"><CapBar r={r} t={t} compact /></span>
          </button>
        ))}
      </div>
      <div className="mt-1.5 flex items-center justify-between" data-testid="wt-pagination">
        <span className={mono}>{view.length} · {page + 1}/{pages}</span>
        <span className="flex gap-1">
          <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)} className={`${field} disabled:opacity-30`} data-testid="wt-prev">←</button>
          <button type="button" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className={`${field} disabled:opacity-30`} data-testid="wt-next">→</button>
        </span>
      </div>
    </section>
  );
}
